<script setup lang="ts">
import { computed } from "vue"
import { useForm } from "@inertiajs/vue3"
import AppLayout from "@/layouts/AppLayout.vue"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"

defineOptions({
    layout: [AppLayout],
})

type OnboardingQuestion = {
    id: string
    question: string
    options: string[]
}

type Props = {
    manualSections: { title: string; content: string }[]
    exam: {
        requiredQuestions: number
        passingPercentage: number
        questions: OnboardingQuestion[]
    }
    status: {
        manualReadAt: string | null
        onboardedAt: string | null
    }
    result: {
        passed: boolean | null
        score: number | null
        total: number | null
        passingScore: number | null
    }
    links: {
        manual_path: string
        exam_path: string
    }
}

const props = defineProps<Props>()

const manualForm = useForm({})
const examForm = useForm({
    answers: {} as Record<string, string>,
})

const manualCompleted = computed(() => Boolean(props.status.manualReadAt))
const onboarded = computed(() => Boolean(props.status.onboardedAt))

function completeManual() {
    manualForm.post(props.links.manual_path)
}

function submitExam() {
    examForm.post(props.links.exam_path)
}
</script>

<template>
    <div class="my-8 space-y-6">
        <Card>
            <CardHeader>
                <CardTitle>Barista Onboarding</CardTitle>
                <CardDescription>Finish the manual and pass the random exam to complete onboarding.</CardDescription>
            </CardHeader>
            <CardContent class="space-y-3 text-sm">
                <p v-if="onboarded" class="text-green-700 font-medium">Onboarding completed. You are now fully onboarded as a barista.</p>
                <p v-else-if="manualCompleted" class="text-amber-700 font-medium">Manual complete. Proceed to the randomized exam below.</p>
                <p v-else class="text-gray-700">Read all sections in the manual, then confirm to unlock the exam.</p>
            </CardContent>
        </Card>

        <Card>
            <CardHeader>
                <CardTitle>Barista Manual</CardTitle>
                <CardDescription>Review every section carefully before taking the exam.</CardDescription>
            </CardHeader>
            <CardContent class="space-y-6">
                <div v-for="section in manualSections" :key="section.title" class="space-y-2">
                    <h3 class="text-base font-semibold">{{ section.title }}</h3>
                    <p class="text-sm text-gray-700">{{ section.content }}</p>
                </div>

                <div class="pt-2">
                    <Button v-if="!manualCompleted" @click="completeManual" :disabled="manualForm.processing">I finished reading the manual</Button>
                    <p v-else class="text-sm text-green-700">Manual marked as completed.</p>
                </div>
            </CardContent>
        </Card>

        <Card v-if="manualCompleted && !onboarded">
            <CardHeader>
                <CardTitle>Randomized Exam</CardTitle>
                <CardDescription>{{ exam.requiredQuestions }} questions are required. Passing score: {{ exam.passingPercentage }}%.</CardDescription>
            </CardHeader>
            <CardContent class="space-y-5">
                <p v-if="result.passed === false" class="text-sm text-red-700">
                    You scored {{ result.score }}/{{ result.total }}. You need at least {{ result.passingScore }} correct answers to pass. A new random exam has been generated.
                </p>

                <p v-if="examForm.errors.exam" class="text-sm text-red-700">{{ examForm.errors.exam }}</p>

                <form @submit.prevent="submitExam" class="space-y-6">
                    <div v-for="(question, index) in exam.questions" :key="question.id" class="space-y-3 rounded-md border p-4">
                        <p class="font-medium">{{ index + 1 }}. {{ question.question }}</p>
                        <div class="space-y-2">
                            <label v-for="option in question.options" :key="option" class="flex items-center gap-2 text-sm">
                                <input v-model="examForm.answers[question.id]" type="radio" :name="question.id" :value="option" class="h-4 w-4" required />
                                <span>{{ option }}</span>
                            </label>
                        </div>
                    </div>

                    <Button type="submit" :disabled="examForm.processing">Submit exam</Button>
                </form>
            </CardContent>
        </Card>
    </div>
</template>
